/**
 * Icon component for rendering pre-imported SVG icons with consistent styling and props.
 *
 * @module Icon
 * @description
 * This component provides a simple way to display SVG icons in your application by name.
 * Only the icons explicitly imported and registered in the `icons` object will be available.
 *
 * @example
 * // Render a check icon at size 32px
 * <Icon name="circle-check" size={32} className="text-green-500" />
 *
 * // Render a warning icon with default size (24px)
 * <Icon name="triangle-exclamation" />
 */

import {
  IconAiTranslate,
  IconApiConnection,
  IconArrowBoxLeft,
  IconArrowBoxRight,
  IconArrowCornerDownLeft,
  IconArrowCornerRightDown,
  IconArrowDown,
  IconArrowDownWall,
  IconArrowRedoDown,
  IconArrowRight,
  IconArrowRotateCounterClockwise,
  IconArrowRotateRightLeft,
  IconArrowSplitDown1,
  IconArrowsRepeatRightLeft,
  IconArrowUndoUp,
  IconArrowUp,
  IconArrowUpRight,
  IconAutoFlash,
  IconBarsThree,
  IconBold,
  IconBookmark,
  IconBookmarkPlus,
  IconBox2AltFill,
  IconBrackets1,
  IconBrackets2,
  IconBrain1,
  IconBranch,
  IconBubbles,
  IconBubbleText,
  IconBug,
  IconBuildings,
  IconBulletList,
  IconCalendarClock,
  IconChainLink4,
  IconChart1,
  IconChart5,
  IconChecklist,
  IconCheckmark1,
  IconCheckmark1Small,
  IconChevronDownMedium,
  IconChevronDownSmall,
  IconChevronGrabberHorizontal,
  IconChevronGrabberVertical,
  IconChevronLeft,
  IconChevronLeftSmall,
  IconChevronRight,
  IconChevronRightMedium,
  IconChevronRightSmall,
  IconChevronTopMedium,
  IconCircleCheck,
  IconCircleDashed,
  IconCircleInfo,
  IconCircleMinus,
  IconCirclePlus,
  IconCircleQuestionmark,
  IconCircleX,
  IconClipboard,
  IconClockSquare,
  IconCloudOff,
  IconCode,
  IconConnectors1,
  IconConsole,
  IconContacts,
  IconCornerRadius,
  IconCrossLarge,
  IconCrossMedium,
  IconCrossSmall,
  IconCrown,
  IconCurrencyDollar,
  IconDashboardFast,
  IconDeepSearch,
  IconDotGrid1x3Horizontal,
  IconDotGrid1x3Vertical,
  IconDoupleCheck,
  IconElectrocardiogram,
  IconExclamationTriangle,
  IconExpandSimple,
  IconEyeClosed,
  IconEyeOpen,
  IconEyeSlash,
  IconFileText,
  IconFilter1,
  IconFilterTimeline,
  IconFlag1,
  IconFocusSquare,
  // `folder` uses the filled variant to match the rest of the sidebar icons,
  // which all import from this filled set.
  IconFolder1 as IconFolder1Filled,
  IconFolderOpen,
  IconGlobe,
  IconHashtag,
  IconHistory,
  IconHome,
  IconImageAltText,
  IconImageSparkle,
  IconItalic,
  IconKey1,
  IconLayersThree,
  IconLayoutGrid2,
  IconLightBulb,
  IconLightBulbSimple,
  IconListBullets,
  IconListSparkle,
  IconLoader,
  IconLock,
  IconMacintosh,
  IconMagicWand2,
  IconMagnifyingGlass,
  IconMoon,
  IconNote1,
  IconNumberedList,
  IconPageCheck,
  IconPageSearchLines,
  IconPageText,
  IconPageTextAdd,
  IconPaperclip1,
  IconPaperPlane,
  IconPayment,
  IconPencil,
  IconPencilLine,
  IconPeople,
  IconPhone,
  IconPlanning,
  IconPlay,
  IconPlusSmall,
  IconPromptSuggestion,
  IconPuzzle,
  IconQuickSearch,
  IconRobot,
  IconRocket,
  IconScanTextSparkle,
  IconScratchCard,
  IconSearchIntelligence,
  IconServer,
  IconServer2,
  IconSettingsGear1,
  IconSettingsGear2,
  IconSettingsSliderThree,
  IconSettingsToggle1,
  IconShield,
  IconShieldCheck,
  IconSidebar,
  IconSlidesWide,
  IconSparklesThree,
  IconSparklesTwo,
  IconSquareBehindSquare1,
  IconSquareCheck,
  IconSquarePlaceholder,
  IconSquarePlus,
  IconStar as IconStarFilled,
  IconStepBack,
  IconStop,
  IconStrikeThrough,
  IconSun,
  IconText1,
  IconTextIndicator,
  IconThumbsDown,
  IconThumbsUp,
  IconToolbox,
  IconTouch,
  IconTrashCan,
  IconUnderline,
  IconUser,
  IconWindowCursor,
  IconWorld,
  IconZap
} from "@central-icons-react/round-filled-radius-2-stroke-1.5"
import {
  IconArCube3 as IconArCube1Outlined,
  IconArrowRight as IconArrowRightOutlined,
  IconArrowRotateClockwise as IconArrowRotateClockwiseOutlined,
  IconArrowUpRight as IconArrowUpRightOutlined,
  IconArrowUpWall as IconArrowUpWallOutlined,
  IconBubble3 as IconBubble3Outlined,
  IconBubbleQuotes as IconBubbleQuotesOutlined,
  IconCheckmark1Small as IconCheckmark1SmallOutlined,
  IconCircleDashed as IconCircleDashedOutlined,
  IconCircleInfo as IconCircleInfoOutlined,
  IconClipboard as IconClipboardOutlined,
  IconClock as IconClockOutlined,
  IconCloudUpload,
  IconDollar,
  IconFileDownload as IconFileDownloadOutlined,
  IconFileText as IconFileTextOutlined,
  IconFilter2,
  IconLightBulbSimple as IconLightBulbSimpleOutlined,
  IconShift as IconShiftOutlined,
  IconSquareBehindSquare1 as IconSquareBehindSquare1Outlined,
  IconStar as IconStarOutlined,
  IconSuitcaseWork as IconSuitcaseWorkOutlined,
  IconTrashCan as IconTrashCanOutlined,
  IconTrashCanSimple as IconTrashCanSimpleOutlined,
  IconUserGroup
} from "@central-icons-react/round-outlined-radius-2-stroke-1.5"

/**
 * Mapping of icon names to their respective SVG components.
 * Only icons added here will be available for use.
 */
export const icons = {
  "suitcase-work": IconSuitcaseWorkOutlined,
  "api-connection": IconApiConnection,
  "chevron-right": IconChevronRight,
  "ar-cube-1": IconArCube1Outlined,
  "arrow-box-left": IconArrowBoxLeft,
  "arrow-box-right": IconArrowBoxRight,
  "arrow-corner-down-left": IconArrowCornerDownLeft,
  "arrow-corner-right-down": IconArrowCornerRightDown,
  "arrow-down": IconArrowDown,
  "arrow-down-wall": IconArrowDownWall,
  "arrow-redo-up": IconArrowRedoDown,
  "arrow-right": IconArrowRight,
  "arrow-right-outlined": IconArrowRightOutlined,
  "arrow-rotate-clockwise-outlined": IconArrowRotateClockwiseOutlined,
  "arrow-rotate-counter-clockwise": IconArrowRotateCounterClockwise,
  "arrow-rotate-right-left": IconArrowRotateRightLeft,
  "arrow-split": IconArrowSplitDown1,
  "arrow-undo-up": IconArrowUndoUp,
  "arrow-up": IconArrowUp,
  "arrow-up-right": IconArrowUpRight,
  "arrow-up-right-outlined": IconArrowUpRightOutlined,
  "arrow-up-wall-outlined": IconArrowUpWallOutlined,
  "arrows-repeat-right-left": IconArrowsRepeatRightLeft,
  "auto-flash": IconAutoFlash,
  "bars-three": IconBarsThree,
  bold: IconBold,
  bookmark: IconBookmark,
  "bookmark-plus": IconBookmarkPlus,
  "box-2-alt-fill": IconBox2AltFill,
  "brackets-1": IconBrackets1,
  "brackets-2": IconBrackets2,
  branch: IconBranch,
  "brain-1": IconBrain1,
  "bubble-3": IconBubble3Outlined,
  "bubble-quotes": IconBubbleQuotesOutlined,
  "bubble-text": IconBubbleText,
  bubbles: IconBubbles,
  bug: IconBug,
  buildings: IconBuildings,
  "bullet-list": IconBulletList,
  "calendar-clock": IconCalendarClock,
  "chain-link-4": IconChainLink4,
  chart: IconChart1,
  "chart-5": IconChart5,
  "checkmark-1": IconCheckmark1,
  "checkmark-1-small": IconCheckmark1Small,
  "checkmark-1-small-outlined": IconCheckmark1SmallOutlined,
  checklist: IconChecklist,
  "chevron-down-medium": IconChevronDownMedium,
  "chevron-down-small": IconChevronDownSmall,
  "chevron-grabber-horizontal": IconChevronGrabberHorizontal,
  "chevron-grabber-vertical": IconChevronGrabberVertical,
  "chevron-left": IconChevronLeft,
  "chevron-left-small": IconChevronLeftSmall,
  "chevron-right-medium": IconChevronRightMedium,
  "chevron-right-small": IconChevronRightSmall,
  "chevron-top-medium": IconChevronTopMedium,
  "circle-check": IconCircleCheck,
  "circle-dashed": IconCircleDashed,
  "circle-dashed-outline": IconCircleDashedOutlined,
  "circle-info": IconCircleInfo,
  "circle-info-outline": IconCircleInfoOutlined,
  "circle-minus": IconCircleMinus,
  "circle-plus": IconCirclePlus,
  "circle-questionmark": IconCircleQuestionmark,
  "circle-x": IconCircleX,
  clipboard: IconClipboard,
  "clipboard-outlined": IconClipboardOutlined,
  clock: IconClockOutlined,
  "cloud-off": IconCloudOff,
  "cloud-upload": IconCloudUpload,
  "clock-square": IconClockSquare,
  code: IconCode,
  console: IconConsole,
  contacts: IconContacts,
  "corner-radius": IconCornerRadius,
  "cross-medium": IconCrossMedium,
  "cross-large": IconCrossLarge,
  "cross-small": IconCrossSmall,
  crown: IconCrown,
  "currency-dollar": IconCurrencyDollar,
  "dollar-circle": IconDollar,
  "dashboard-fast": IconDashboardFast,
  "deep-search": IconDeepSearch,
  "dot-grid-1x3-horizontal": IconDotGrid1x3Horizontal,
  "dot-grid-1x3-vertical": IconDotGrid1x3Vertical,
  "douple-check": IconDoupleCheck,
  "eye-closed": IconEyeClosed,
  "eye-open": IconEyeOpen,
  "ai-task": IconListSparkle,
  "ai-translate": IconAiTranslate,
  electrocardiogram: IconElectrocardiogram,
  "eye-slash": IconEyeSlash,
  "flag-1": IconFlag1,
  "focus-square": IconFocusSquare,
  "expand-simple": IconExpandSimple,
  "file-download": IconFileDownloadOutlined,
  "file-text": IconFileText,
  "file-text-outlined": IconFileTextOutlined,
  filter: IconFilter1,
  "filter-lines": IconFilter2,
  filtertimeline: IconFilterTimeline,
  folder: IconFolder1Filled,
  "folder-open": IconFolderOpen,
  globus: IconGlobe,
  hashtag: IconHashtag,
  history: IconHistory,
  home: IconHome,
  "image-alt-text": IconImageAltText,
  "image-sparkle": IconImageSparkle,
  italic: IconItalic,
  key: IconKey1,
  "layers-three": IconLayersThree,
  "light-bulb": IconLightBulb,
  "light-bulb-simple": IconLightBulbSimple,
  "light-bulb-simple-outlined": IconLightBulbSimpleOutlined,
  "layout-grid-2": IconLayoutGrid2,
  "list-bullets": IconListBullets,
  "list-sparkle": IconListSparkle,
  loader: IconLoader,
  lock: IconLock,
  macintosh: IconMacintosh,
  "magic-wand-2": IconMagicWand2,
  "magnifying-glass": IconMagnifyingGlass,
  moon: IconMoon,
  "note-1": IconNote1,
  "numbered-list": IconNumberedList,
  "page-check": IconPageCheck,
  "page-search-lines": IconPageSearchLines,
  "page-text": IconPageText,
  "page-text-add": IconPageTextAdd,
  "paper-plane": IconPaperPlane,
  paperclip: IconPaperclip1,
  payment: IconPayment,
  pencil: IconPencil,
  "pencil-line": IconPencilLine,
  people: IconPeople,
  phone: IconPhone,
  planning: IconPlanning,
  play: IconPlay,
  "plus-small": IconPlusSmall,
  "prompt-suggestion": IconPromptSuggestion,
  puzzle: IconPuzzle,
  robot: IconRobot,
  rocket: IconRocket,
  "scan-text-sparkle": IconScanTextSparkle,
  "scratch-card": IconScratchCard,
  search: IconQuickSearch,
  "search-intelligence": IconSearchIntelligence,
  server: IconServer,
  "server-2": IconServer2,
  "settings-gear": IconSettingsGear1,
  "settings-gear-2": IconSettingsGear2,
  "settings-slider-three": IconSettingsSliderThree,
  "settings-toggle-1": IconSettingsToggle1,
  shield: IconShield,
  "shield-check": IconShieldCheck,
  shift: IconShiftOutlined,
  "sidebar-panel": IconSidebar,
  "slides-wide": IconSlidesWide,
  "sparkles-three": IconSparklesThree,
  "sparkles-two": IconSparklesTwo,
  spinner: IconLoader,
  "square-behind-square-1": IconSquareBehindSquare1,
  "square-behind-square-1-outlined": IconSquareBehindSquare1Outlined,
  "square-check": IconSquareCheck,
  "square-placeholder": IconSquarePlaceholder,
  "square-plus": IconSquarePlus,
  star: IconStarOutlined,
  "star-filled": IconStarFilled,
  "step-back": IconStepBack,
  stop: IconStop,
  strikethrough: IconStrikeThrough,
  sun: IconSun,
  "text-1": IconText1,
  "text-indicator": IconTextIndicator,
  "thumbs-down": IconThumbsDown,
  "thumbs-up": IconThumbsUp,
  toolbox: IconToolbox,
  touch: IconTouch,
  trash: IconTrashCan,
  "trash-can-outlined": IconTrashCanOutlined,
  "trash-can-simple-outlined": IconTrashCanSimpleOutlined,
  "triangle-exclamation": IconExclamationTriangle,
  underline: IconUnderline,
  "window-cursor": IconWindowCursor,
  workflow: IconConnectors1,
  world: IconWorld,
  user: IconUser,
  "user-group": IconUserGroup,
  zap: IconZap
}
